/**
 * Fee-schedule form: percent/dollar input -> the bps/minor units R8 actually compares against.
 *
 * WHY this conversion cannot use float math: `rate_bps` and `flat_minor` are integers a seller
 * never sees directly — they type "2.9" (percent) and "0.30" (dollars) — and `2.9 * 100` is
 * 289.99999999999994 in IEEE 754, not 290. money.ts already refuses to scale a minor-unit string
 * with a float divide for *display*; this module is the mirror image for *input*, done the same
 * way: decimal digits are shifted with string surgery, never multiplied as a JS number.
 * `flat_minor` additionally scales by the currency's own ISO-4217 exponent via money.ts's
 * `currencyExponent`, so a 0- or 3-decimal currency is not silently treated as cents.
 */
import { currencyExponent, isCurrencyCode } from "./money";

/**
 * The two kinds this screen ALWAYS offers, because every platform has them and a seller can be
 * expected to know their own sale rate. Anything else — Amazon's PLATFORM_FEE, say — gets a
 * section only when the store actually has lines of that kind, so the form never asks a Shopify
 * seller to price a charge their platform does not make (`feeScheduleSections`).
 */
export const LINE_KINDS = ["SALE", "REFUND"] as const;
export type LineKind = (typeof LINE_KINDS)[number];

/** The canonical `line_kind` enum (DATA_MODEL §2). Validation accepts any of them; the form only
 *  ever submits one it derived from the store's own data or from LINE_KINDS above. */
export const ALL_LINE_KINDS = [
  "SALE", "REFUND", "DISPUTE_HOLD", "DISPUTE_RELEASE", "DISPUTE_FEE", "RESERVE_HOLD", "RESERVE_RELEASE",
  "ADJUSTMENT", "PLATFORM_FEE", "SHIPPING_LABEL", "TRANSFER", "PAYOUT_RETRY", "TAX", "OTHER",
] as const;

/** 100.00% expressed in basis points — the top of the valid `rate_bps` range. */
const MAX_RATE_BPS = 10_000;
/** A basis point is a hundredth of a percent, so a percent input carries at most 2 decimals. */
const BPS_DECIMALS = 2;

/** No sign, no exponent notation — exactly what a percent-or-dollars text field should hold. */
const UNSIGNED_DECIMAL = /^(\d+)(?:\.(\d+))?$/;

/**
 * Unsigned decimal string -> integer string at exactly `decimals` fractional places, by padding
 * and concatenating digits rather than multiplying. Returns null for anything that is not a
 * plain non-negative decimal, or that carries more fractional digits than `decimals` allows — a
 * fraction of a cent, or of a basis point, is rejected rather than silently rounded away.
 */
export function decimalToIntegerString(input: string, decimals: number): string | null {
  const match = UNSIGNED_DECIMAL.exec(input.trim());
  if (!match) return null;
  const whole = match[1] as string;
  const frac = match[2] ?? "";
  if (frac.length > decimals) return null;
  const digits = `${whole}${frac.padEnd(decimals, "0")}`;
  const stripped = digits.replace(/^0+(?=\d)/, "");
  return stripped;
}

/** "2.9" -> 290, "2.95" -> 295. Rejects a fractional basis point ("2.905") rather than rounding it. */
export function percentToBps(percent: string): number | null {
  const digits = decimalToIntegerString(percent, BPS_DECIMALS);
  if (digits === null) return null;
  const bps = Number(digits);
  return bps > MAX_RATE_BPS ? null : bps;
}

/** "0.30" -> "30" (cents). Scales by the currency's own exponent, never a hardcoded 2. */
export function dollarsToFlatMinor(dollars: string, currency: string | null | undefined): string | null {
  return decimalToIntegerString(dollars, currencyExponent(currency));
}

/**
 * Basis points back to a percent string for display — "295" -> "2.95". `rate_bps` is a small
 * plain number (the API contract, not a bigint minor-unit field), so this is presentation, not
 * a repeat of the money-safety problem above; it mirrors money.ts's own minor-to-decimal
 * scaling anyway (bps are "minor units of a percent", exponent 2) so the two read the same way.
 */
export function bpsToPercentString(bps: number): string {
  const sign = bps < 0 ? "-" : "";
  const digits = Math.abs(bps).toFixed(0);
  const padded = digits.padStart(BPS_DECIMALS + 1, "0");
  const whole = padded.slice(0, padded.length - BPS_DECIMALS).replace(/^0+(?=\d)/, "");
  return `${sign}${whole}.${padded.slice(padded.length - BPS_DECIMALS)}`;
}

export interface FeeScheduleFormInput {
  lineKind: string;
  /** The platform charge this rate prices, or null for every line of the kind (FeeSchedule). */
  nativeSubtype?: string | null;
  /** e.g. "2.9" for 2.9%, exactly as typed. */
  ratePercent: string;
  /** e.g. "0.30" for 30 cents, exactly as typed. */
  flatDollars: string;
  currency: string;
  /** yyyy-mm-dd, from an <input type="date">. */
  effectiveFrom: string;
  source: string;
}

export interface FeeScheduleFormValue {
  line_kind: string;
  native_subtype: string | null;
  rate_bps: number;
  flat_minor: string;
  currency: string;
  effective_from: string;
  source: string;
}

export type FeeScheduleFormErrors = Partial<Record<keyof FeeScheduleFormInput, string>>;

export type FeeScheduleValidation =
  | { ok: true; value: FeeScheduleFormValue }
  | { ok: false; errors: FeeScheduleFormErrors };

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Client-side mirror of the server's checks, so the common typo (a percent over 100, a
 * negative flat fee, a missing source) is caught before a round trip. This is a courtesy, not
 * the source of truth — the same server-authority pattern Billing.tsx follows: the caller still
 * surfaces a 400 from the API rather than treating this function's `ok: true` as final.
 */
export function validateFeeScheduleForm(input: FeeScheduleFormInput): FeeScheduleValidation {
  const errors: FeeScheduleFormErrors = {};

  if (!(ALL_LINE_KINDS as readonly string[]).includes(input.lineKind)) errors.lineKind = "Unknown line kind.";

  const rate_bps = percentToBps(input.ratePercent);
  if (rate_bps === null) errors.ratePercent = "Enter a rate between 0 and 100, e.g. 2.9 (percent, not basis points).";

  const flat_minor = dollarsToFlatMinor(input.flatDollars, input.currency);
  if (flat_minor === null) errors.flatDollars = "Enter a flat fee of 0 or more, in dollars, e.g. 0.30.";

  if (!isCurrencyCode(input.currency)) errors.currency = "Enter a 3-letter ISO currency code, e.g. USD.";

  if (!DATE_PATTERN.test(input.effectiveFrom)) errors.effectiveFrom = "Pick the date this rate takes effect.";

  if (input.source.trim() === "") errors.source = "Say where this rate came from, e.g. \"Shopify Basic US plan\".";

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    value: {
      line_kind: input.lineKind,
      native_subtype: input.nativeSubtype ?? null,
      rate_bps: rate_bps as number,
      flat_minor: flat_minor as string,
      currency: input.currency,
      effective_from: input.effectiveFrom,
      source: input.source.trim(),
    },
  };
}

/**
 * The slices of a store's fees that NOTHING checks — the input to the overview's "fee checking is
 * off" notice.
 *
 * Kept here, as a pure function over the summary, for the same reason the rest of this module is:
 * the component that renders it has no test harness, and the interesting part is not the markup
 * but the arithmetic — `lines` and `priced` are bigint-shaped decimal strings from Postgres and
 * comparing them as JS numbers would quietly go wrong on a store big enough to matter.
 */
export interface FeeCoverageEntry {
  line_kind: string;
  native_subtype: string | null;
  currency: string;
  lines: string;
  priced: string;
}

/** How a coverage row names itself on screen: "PLATFORM_FEE / FBAInventoryFee in USD". */
export function feeCoverageLabel(c: FeeCoverageEntry): string {
  const kind = c.native_subtype ? `${c.line_kind} / ${c.native_subtype}` : c.line_kind;
  return `${kind} in ${c.currency}`;
}

export function feeCoverageGaps(coverage: readonly FeeCoverageEntry[]): FeeCoverageEntry[] {
  return coverage.filter((c) => BigInt(c.priced) < BigInt(c.lines));
}

/** How many fee-bearing lines no schedule prices, across every gap. Zero means R8 sees everything. */
export function uncheckedFeeLines(coverage: readonly FeeCoverageEntry[]): bigint {
  return feeCoverageGaps(coverage).reduce((n, c) => n + (BigInt(c.lines) - BigInt(c.priced)), 0n);
}

/**
 * The sections the fee screen shows, in order: the two universal kinds, then one per platform
 * charge the STORE actually has fee-bearing lines for.
 *
 * Derived from data rather than typed by the seller, because a subtype is the platform's own
 * vocabulary — asking someone to spell `FBAInventoryFee` correctly into a text box is a way to
 * create a schedule that silently prices nothing. Existing schedules are folded in too, so a row
 * already configured keeps its section even after the lines it prices scroll out of the window.
 *
 * Only UNPRICED coverage adds a section, and that limit is not an optimisation. `native_subtype`
 * is whatever the platform put there and it does not mean the same thing everywhere: Shopify sends
 * a source type and Amazon a charge name, but TikTok sends `unsettled_reason` — a FULFILMENT
 * STATUS that changes as the order moves. Building a section from every covered row would give a
 * TikTok seller headings like "Order delivered and awaiting settlement fee" that appear and vanish
 * as orders progress. A row that is already priced needs no section anyway, so the narrower rule is
 * both safer and the one that matches what the overview offers to fix.
 */
export interface FeeScheduleSection { lineKind: string; nativeSubtype: string | null }

export function feeScheduleSections(
  coverage: readonly FeeCoverageEntry[], schedules: readonly { line_kind: string; native_subtype: string | null }[],
): FeeScheduleSection[] {
  const seen = new Map<string, FeeScheduleSection>();
  const add = (lineKind: string, nativeSubtype: string | null): void => {
    const key = `${lineKind}\u0000${nativeSubtype ?? ""}`;
    if (!seen.has(key)) seen.set(key, { lineKind, nativeSubtype });
  };
  for (const kind of LINE_KINDS) add(kind, null);
  for (const s of schedules) add(s.line_kind, s.native_subtype);
  for (const c of feeCoverageGaps(coverage)) add(c.line_kind, c.native_subtype);
  return [...seen.values()];
}

/** The heading for one section: "Sale", "Refund", or the platform's own name for the charge. */
export function sectionLabel(section: FeeScheduleSection): string {
  if (section.nativeSubtype) return section.nativeSubtype;
  return section.lineKind === "SALE" ? "Sale" : section.lineKind === "REFUND" ? "Refund" : section.lineKind;
}
